import { isNull } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { PageToggleButton } from '../../../shared/button/PageToggleButton';
import { PlusButton } from '../../../shared/button/PlusButton';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Calculator, CaretDown, CaretSide } from '../../../shared/icons';
import { TypeDeleteConfirmationModal } from '../../../shared/modal/TypeDeleteConfirmationModal';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { CalculatorManagerPage, CalculatorPromptDB, CalculatorPromptUI, fetchCalculatorPromptsStarted, getAllCalculatorFields, getCalculatorPromptUpdated, getCurrentCalculatorPromptOrderForType, getSavedCalculatorPromptOrderForType, savePromptOrderStarted, setCalculatorManagerPage, setCalculatorPromptOrder, updateCalculatorPrompt, updateCalculatorPromptOrder, upsertCalculatorPromptStarted } from '../../calculator/store';
import { getAllClients } from '../../clients/store';
import { CalculatorPromptModal } from '../../shared/CalculatorPromptModal';
import { CalculatorPromptReorderModal } from '../../shared/CalculatorPromptReorderModal';
import { CalculatorPrompts } from '../../shared/CalculatorPrompts';
import { deleteCalculatorPromptStarted, getAllCalculatorPrompts, getCalculatorPromptToDelete, getIsDeletingCalculatorPrompts, getSelectedCalculatorPrompt, selectCalculatorPrompt, toggleDeleteCalculatorPromptConfirmationModal } from '../store';
import styles from './CalculatorPrompt.module.scss';

const NAV_HEADER_MARGINS = 160;

export const CalculatorPrompt: React.FC = () => {
    const [openSections, setOpenSections] = useState<string[]>([]);
    const [reorderModalOpen, setReorderModalOpen] = useState<boolean>(false);
    const [selectedClient, setSelectedClient] = useState<DropdownOption>({ value: 'system', label: 'System' });

    const dispatch = useAppDispatch();
    const fieldTypeOptions = useAppSelector(getAllCalculatorFields);

    const calculatorPrompts = useAppSelector(getAllCalculatorPrompts);
    const selectedCalculatorPrompt = useAppSelector(getSelectedCalculatorPrompt);
    const calculatorPromptIdToDelete = useAppSelector(getCalculatorPromptToDelete);
    const isDeleting = useAppSelector(getIsDeletingCalculatorPrompts);
    const currentTypePromptOrder = useAppSelector(getCurrentCalculatorPromptOrderForType);
    const savedTypePromptOrder = useAppSelector(getSavedCalculatorPromptOrderForType);
    const clients = useAppSelector(getAllClients);
    const hasUpdated = useAppSelector(getCalculatorPromptUpdated);
    const testId = 'ai-calculator-prompt';

    const savePromptOrder = useCallback(() => dispatch(savePromptOrderStarted()), [dispatch]);
    const setPromptOrder = useCallback((promptOrder: CalculatorPromptDB[]) => dispatch(setCalculatorPromptOrder(promptOrder)), [dispatch]);
    const updatePromptOrder = useCallback((promptOrder: CalculatorPromptDB[]) => dispatch(updateCalculatorPromptOrder(promptOrder)), [dispatch]);
    const openReorderModal = useCallback((promptOrder: CalculatorPromptDB[]) => {
        setReorderModalOpen(true);
        setPromptOrder(promptOrder);
    }, [setReorderModalOpen, setPromptOrder]);
    const closeReorderModal = useCallback(() => {
        setReorderModalOpen(false);
        setPromptOrder([]);
    }, [setReorderModalOpen, setPromptOrder]);

    const selectPrompt = useCallback((calculatorPromptId?: number) => dispatch(selectCalculatorPrompt(calculatorPromptId)), [dispatch]);
    const createNewPrompt = useCallback(() => selectPrompt(), [selectPrompt]);

    const deletePrompt = useCallback(() => dispatch(deleteCalculatorPromptStarted()), [dispatch]);
    const toggleDeleteConfirmationModal = useCallback((calculatorPromptId: number | null) => dispatch(toggleDeleteCalculatorPromptConfirmationModal(calculatorPromptId)), [dispatch]);

    const [screenWidth, screenHeight] = useWindowResize();

    const wrapperHeight = useMemo(() => screenHeight - NAV_HEADER_MARGINS, [screenHeight]);

    const isSystemPrompts = useMemo(() => selectedClient.value === 'system', [selectedClient]);

    const getRelevantPrompts = useCallback((fieldType: string) => calculatorPrompts.filter(({ calculatorFieldType }) => calculatorFieldType === fieldType).sort((a, b) => a.priorityOrder - b.priorityOrder), [calculatorPrompts]);

    const getSectionOpen = useCallback((calculatorFieldType: string) => openSections.includes(calculatorFieldType), [openSections]);
    const getSectionOpenIcon = useCallback((isOpen: boolean) => isOpen ? CaretDown : CaretSide, []);
    const toggleSection = useCallback((calculatorFieldType: string) => {
        const isOpen = getSectionOpen(calculatorFieldType);
        if (isOpen) {
            return setOpenSections(openSections => openSections.filter(type => type !== calculatorFieldType));
        }
        return setOpenSections(openSections => ([...openSections, calculatorFieldType]));
    }, [getSectionOpen, setOpenSections]);

    const goToManager = useCallback(() => dispatch(setCalculatorManagerPage(CalculatorManagerPage.MANAGER)), [dispatch]);

    const clientDropdownOptions: DropdownOption[] = useMemo(() => {
        const clientOptions = clients.map(({ clientName, clientId }) => ({ value: clientId!.toString(), label: clientName }));
        return [{ value: 'system', label: 'System' }, ...clientOptions];
    }, [clients]);

    const updateSelectedClient = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        if (!isNull(dropdownValue)) {
            const value = (dropdownValue as DropdownOption).value;
            const clientId = value === 'system' ? undefined : parseInt(value);
            dispatch(fetchCalculatorPromptsStarted(clientId));
        }
        setSelectedClient((dropdownValue as DropdownOption));
        setOpenSections([]);
    }, [dispatch]);

    const closeModal = useCallback(() => dispatch(selectCalculatorPrompt(null)), [dispatch]);
    const updatePrompt = useCallback((calculatorPrompt: CalculatorPromptUI) => dispatch(updateCalculatorPrompt(calculatorPrompt)), [dispatch]);
    const saveCalculatorPrompt = useCallback(() => dispatch(upsertCalculatorPromptStarted()), [dispatch]);

    useEffect(() => () => {
        dispatch(fetchCalculatorPromptsStarted());
    }, [dispatch]);

    return (
        <div className={styles.calculatorPromptWrapper} data-testid={`${testId}-wrapper`}>
            <div className={styles.calculatorPromptHeaderWrapper}>
                <div className={styles.calculatorPromptTitleButtonWrapper}>
                    <div className={styles.calculatorPromptHeaderTitle} data-testid={`${testId}-title`}>Calculator Prompts</div>
                    <PlusButton onClick={createNewPrompt} testId={testId} fontSize={22} />
                    <div className={styles.clientDropdownWrapper}>
                        <Dropdown
                            options={clientDropdownOptions}
                            value={selectedClient}
                            onChange={updateSelectedClient}
                            isClearable={false}
                        />
                    </div>
                </div>
                <PageToggleButton label='Calculator Manager' onClick={goToManager} icon={Calculator} testId={testId} fontSize={25} />
            </div>
            <div className={styles.calculatorPromptContent} style={{ height: wrapperHeight }}>
                <div className={styles.calculatorPromptsWrapper}>
                    <Scrollable>
                        {fieldTypeOptions.map(({ includeGracePeriod, includeOutput, name, calculatorFieldTypeId }) => {
                            const sectionOpen = getSectionOpen(name);
                            const relevantPrompts = getRelevantPrompts(name);
                            const showReorderIcon = isSystemPrompts && relevantPrompts.length > 1;
                            const showButtons = (isSystem: boolean) => isSystem;
                            return (
                                <CalculatorPrompts
                                    key={calculatorFieldTypeId}
                                    name={name}
                                    sectionOpen={sectionOpen}
                                    showReorderIcon={showReorderIcon}
                                    relevantPrompts={relevantPrompts}
                                    includeOutput={includeOutput}
                                    includeGracePeriod={includeGracePeriod}
                                    openReorderModal={openReorderModal}
                                    showButtons={showButtons}
                                    selectPrompt={selectPrompt}
                                    toggleDeleteConfirmationModal={toggleDeleteConfirmationModal}
                                    getSectionOpenIcon={getSectionOpenIcon}
                                    toggleSection={toggleSection}
                                />
                            );
                        }
                        )}
                    </Scrollable>
                </div>
            </div>
            {!isNull(selectedCalculatorPrompt) &&
                <CalculatorPromptModal
                    width={screenWidth * 0.7}
                    calculatorPrompt={selectedCalculatorPrompt}
                    getRelevantPrompts={getRelevantPrompts}
                    hasUpdated={hasUpdated}
                    closeModal={closeModal}
                    updatePrompt={updatePrompt}
                    saveCalculatorPrompt={saveCalculatorPrompt}
                />
            }
            <CalculatorPromptReorderModal
                isOpen={reorderModalOpen}
                closeModal={closeReorderModal}
                saveOrder={savePromptOrder}
                setOrder={updatePromptOrder}
                savedPromptOrder={savedTypePromptOrder}
                currentPromptOrder={currentTypePromptOrder}
            />
            <TypeDeleteConfirmationModal
                isOpen={!isNull(calculatorPromptIdToDelete)}
                closeModal={() => toggleDeleteConfirmationModal(null)}
                deleteAction={deletePrompt}
                headerLabel='Delete Calculator Prompt'
                icon={Calculator}
                isDeleting={isDeleting}
                testId='calculator-prompt'
                deleteLabel='calculator prompt'
                name=''
                warning='This cannot be undone and will be lost as a few shot example for this calculator field type'
            />
        </div>
    );
};
